import { useState } from 'react';

import {
  Button,
  Center,
  Flex,
  HStack,
  Link,
  PinInput,
  PinInputField,
  Spinner,
  Text,
  useBoolean,
  VStack,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { CognitoUser, ArrowLeftOutline } from '@aslanpay/portal-common-lib';

import useConfirmSignIn from 'app/hooks/useConfirmSignIn';
import useSignIn from 'app/hooks/useSignIn';

import { useAuthenticationStore } from 'app/store';

import { AuthenticationStepProps } from '../types';

function ConfirmLogin({ onSetStep }: AuthenticationStepProps) {
  const queryClient = useQueryClient();

  const authenticationData = useAuthenticationStore((state) => state.authenticationData);
  const setAuthenticationData = useAuthenticationStore((state) => state.setAuthenticationData);
  const resetAuthenticationData = useAuthenticationStore((state) => state.resetAuthenticationData);

  const [confirmError, setConfirmError] = useState('');
  const [resendMessage, setResendMessage] = useState('Code not received?');
  const [isResendDisabled, setIsResendDisabled] = useBoolean();

  const { mutate: signIn, isLoading: isSigningIn } = useSignIn({
    onSuccess: (data: CognitoUser) =>
      setAuthenticationData({ ...authenticationData, cognitoUser: data }),
    onError: () => setConfirmError('Unable to resend verification code. Please try again later.'),
  });

  const { mutate: confirmSignIn, isLoading: isConfirmingSignIn } = useConfirmSignIn({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getAuthenticatedUser'] });
      resetAuthenticationData();
    },
    onError: (error) => {
      let message;

      switch (error.name) {
        case 'CodeMismatchException':
          message = 'Wrong code. Please try again';
          break;
        case 'ExpiredCodeException':
          message = 'Verification code already expired';
          break;
        default:
          message = 'Something went wrong. Please try again';
          break;
      }

      setConfirmError(message);
    },
  });

  const handleConfirmLogin = (code: string) => {
    confirmSignIn({
      cognitoUser: authenticationData.cognitoUser,
      code,
    });
  };

  const handleResendCode = () => {
    signIn({ email: authenticationData.email, password: authenticationData.password });

    setIsResendDisabled.on();
    setResendMessage('A new verification code was sent!');
    setTimeout(() => {
      setResendMessage('Code not received?');
      setIsResendDisabled.off();
    }, 120000);
  };

  return (
    <Flex flexDir="column" mt="35px">
      <Button
        alignSelf="flex-start"
        size="sm"
        variant="link"
        leftIcon={<ArrowLeftOutline boxSize="20px" />}
        onClick={() => onSetStep('checkEmail')}
      >
        Back
      </Button>
      <Text as="h2" textStyle="h2" color="brand.darkPurple" mt="25px">
        Authenticate your account
      </Text>
      <Text as="h5" textStyle="h5Medium" color="brand.mutedPurple" mt="15px" mb="35px">
        Please check your mobile number and enter the verification code below:{' '}
        <Text as="span" color="brand.darkPurple">
          {authenticationData.cognitoUser.challengeParam?.CODE_DELIVERY_DESTINATION}
        </Text>
      </Text>
      {isConfirmingSignIn ? (
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" emptyColor="brand.purpleAlpha30" size="xl" />
        </Center>
      ) : (
        <VStack spacing="10px">
          <HStack spacing="20px" justify="center">
            <PinInput
              placeholder=""
              onComplete={handleConfirmLogin}
              size="lg"
              isInvalid={!!confirmError}
            >
              <PinInputField />
              <PinInputField />
              <PinInputField />
              <PinInputField />
              <PinInputField />
              <PinInputField />
            </PinInput>
          </HStack>
          {confirmError && (
            <Text textStyle="smallMedium" color="system.error">
              {confirmError}
            </Text>
          )}
        </VStack>
      )}
      <VStack mt="35px" spacing={0}>
        <Text textStyle="smallRegular" color="brand.darkGray">
          {resendMessage}
        </Text>
        <Button
          size="sm"
          variant="link"
          onClick={handleResendCode}
          isDisabled={isResendDisabled}
          isLoading={isSigningIn}
        >
          Resend code
        </Button>
      </VStack>
      <VStack mt="35px" spacing={0}>
        <Text textStyle="smallRegular" color="brand.darkGray">
          Having issues?
        </Text>
        <Link
          href="mailto:support@aslan.io"
          target="_blank"
          color="brand.purple"
          textStyle="smallMedium"
          textDecoration="underline"
        >
          Please contact support
        </Link>
      </VStack>
    </Flex>
  );
}

export default ConfirmLogin;
